import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const STORAGE_KEY = "mtbl_lang";

const resources = {
  en: {
    translation: {
      // ============ NAV / HEADER ============
      nav: {
        home: "Home",
        about: "About",
        services: "Services",
        sectors: "Sectors",
        opportunities: "Opportunities",
        impact: "Impact",
        partnerships: "Partnerships",
        insights: "Insights",
        contact: "Contact",
        engageCta: "Engage With Us",
        toggleMenu: "Toggle menu",
      },

      // ============ FOOTER ============
      footer: {
        about:
          "MTBL Management Consultancy is a strategic branding, marketing, and investment advisory firm connecting capital to opportunity across Africa and the Middle East.",
        positioning: "Global in outlook. African at our core.",
        offices: "Offices",
        direct: "Direct",
        copyright: "© {{year}} MTBL Management Consultancy. All rights reserved.",
        poweredBy: "Powered by MTBL Management Consultancy",
        disclaimer:
          "MTBL Management Consultancy provides strategic advisory and partnership facilitation services. We do not provide financial investment advice or manage investment portfolios.",
      },

      // ============ COMMON ============
      common: {
        readMore: "Read more",
        learnMore: "Learn more",
        readPerspective: "Read perspective",
        backToInsights: "Back to Insights",
        allPerspectives: "All perspectives",
        engageWithMtbl: "Engage with MTBL Management Consultancy",
        viewOpportunities: "View Opportunities",
        partnerWithUs: "Partner With Us",
        requestInvestmentBrief: "Request Investment Brief",
        startConversation: "Start a Conversation",
        strategicPartnerships: "Strategic Partnerships",
        investorInquiries: "Investor Inquiries",
        institutionalEngagement: "Institutional Engagement",
        sendMessage: "Send Message",
        sending: "Sending…",
        submitting: "Submitting…",
      },

      // ============ HERO SLIDESHOW ============
      hero: {
        overline: "Established 2012 · Global in outlook · African at our core",
        headlinePart1: "Connecting Capital to",
        headlinePart2: "Opportunity",
        headlinePart3: "Across Africa and Emerging Markets",
        support:
          "Established in 2012, MTBL Management Consultancy engages across opportunities aligned with national development priorities, infrastructure expansion, and long-term economic growth.",
      },

      // ============ HOME PAGE ============
      home: {
        positioning1: "Global in outlook. African at our core.",
        positioning2: "Positioned for high-growth markets.",

        whatWeDoOverline: "What we do",
        whatWeDoTitle:
          "At the intersection of advisory, investment engagement, and cross-border collaboration.",
        whatWeDoP1:
          "MTBL Management Consultancy operates at the intersection of advisory, investment engagement, and cross-border collaboration.",
        whatWeDoP2:
          "We support opportunity identification, positioning, and partnership facilitation across sectors that are central to economic growth and development.",
        whatWeDoLearnMore: "Learn more about MTBL Management Consultancy",

        servicesOverline: "Services",
        servicesTitle: "How we engage.",
        servicesDisclaimer:
          "MTBL Management Consultancy provides strategic advisory and partnership facilitation services. We do not provide financial investment advice or manage investment portfolios.",

        opportunitiesOverline: "Selected opportunities",
        opportunitiesTitle:
          "Opportunities aligned with national development priorities.",
        opportunitiesLead:
          "MTBL Management Consultancy engages in opportunities across sectors aligned with national development priorities and economic transformation. Detailed information is shared on a confidential basis.",
        opportunityLabel: "Opportunity",

        engagementOverline: "Strategic engagement",
        engagementTitle: "Aligned with national development agendas.",
        engagementP1:
          "MTBL Management Consultancy engages in initiatives aligned with national development agendas and large-scale infrastructure programs.",
        engagementP2:
          "Our work includes supporting the sourcing of strategic partners and investors across sectors, including transport, energy, ICT, logistics, housing, and public sector development initiatives.",

        collaborationOverline: "Strategic collaboration",
        collaborationTitle: "Working alongside regional and international partners.",
        collaborationP1:
          "MTBL Management Consultancy operates in collaboration with regional and international partners across multiple markets.",
        collaborationP2:
          "Our engagements include working alongside transaction advisory firms and institutions involved in capital mobilization, partner sourcing, and investment facilitation.",

        whyOverline: "Why MTBL Management Consultancy",
        whyTitle: "A disciplined, relationship-led platform.",

        highlightsOverline: "Highlights",
        highlightsTitle: "A focused record of engagement.",

        impactOverline: "Impact",
        impactTitle: "Growth that contributes to long-term development.",
        impactP1:
          "MTBL Management Consultancy believes sustainable growth should contribute to long-term economic development and meaningful community value.",
        impactP2:
          "We integrate impact into our work by supporting opportunities that contribute to infrastructure, economic participation, and long-term growth.",
        impactQuote:
          "We are developing a structured corporate social responsibility framework focused on empowerment, dignity, and community development.",

        finalCtaOverline: "Engage with us",
        finalCtaTitle:
          "Access to opportunities across emerging markets begins with a conversation.",
        finalCtaLead:
          "We work with investors, institutions, and strategic partners seeking access to opportunities across emerging markets.",
      },

      // ============ ABOUT PAGE ============
      about: {
        overline: "Who We Are",
        title: "Who We Are",
        introP1:
          "MTBL Management Consultancy is a strategic branding, marketing, and investment advisory firm dedicated to unlocking high-impact growth opportunities across Africa and the Middle East.",
        introP2:
          "We connect governments, businesses, and investors through creative solutions, data-driven strategy, and powerful partnerships that transform ideas into scalable, investment-ready opportunities.",
        introP3:
          "Rooted in Africa and headquartered in Sandton, South Africa, MTBL Management Consultancy operates at the intersection of strategy, creativity, and capital, positioning markets, brands, and institutions for sustainable growth and global relevance.",

        valuesOverline: "Our core values",
        valuesTitle:
          "Built on integrity, honesty, and strong ethical standards.",
        valuesP1:
          "At MTBL Management Consultancy, our foundation is built on integrity, honesty, and strong ethical standards.",
        valuesP2:
          "These principles guide every decision we make and shape how we engage with our clients, partners, and communities. We believe that trust is the cornerstone of every successful partnership, and we are committed to delivering with transparency, accountability, and excellence.",

        missionOverline: "Our mission",
        missionTitle:
          "Connecting brands, investors, and businesses with transformative opportunities.",
        missionP1:
          "To connect brands, investors, and businesses with transformative opportunities through branding, marketing, and strategic advisory services, driving long-term economic impact and sustainable growth.",

        visionOverline: "Our vision",
        visionTitle:
          "The leading platform for branding, marketing, and investment facilitation.",
        visionP1:
          "To be the leading platform for branding, marketing, and investment facilitation across Africa and the Middle East, positioning emerging markets as engines of global growth.",

        experienceOverline: "Our experience",
        experienceTitle: "Decades of combined experience across industries.",
        experienceP1:
          "Our team brings decades of combined experience across multiple industries, including telecom, energy, infrastructure, tourism, and finance.",
        experienceP2:
          "We have worked with organizations of all sizes, from startups to multinational corporations, giving us a deep understanding of the unique challenges and opportunities within emerging markets.",
        experienceP3prefix: "With over",
        experienceP3highlight: "$100 million in deal facilitation",
        experienceP3suffix:
          ", MTBL Management Consultancy combines strategic insight with execution capability, delivering results that move markets, attract investment, and create lasting impact.",
        statDealLabel: "Deal facilitation",
        statMarketsLabel: "Markets",
        statEstablishedLabel: "Established",

        ctaTitle: "Explore the opportunities we engage with.",
      },

      // ============ SERVICES PAGE ============
      services: {
        headerOverline: "Services",
        headerTitle: "Advisory, positioning, engagement, and facilitation.",
        headerLead:
          "We support investors, institutions, and strategic partners across the full lifecycle of opportunity engagement, from identification to structured positioning and long-term collaboration.",
        itemLabel: "Service",
        disclaimer:
          "MTBL Management Consultancy provides strategic advisory and partnership facilitation services. We do not provide financial investment advice or manage investment portfolios.",
        ctaTitle: "Engage with MTBL Management Consultancy on a specific mandate.",
      },

      // ============ SECTORS PAGE ============
      sectors: {
        headerOverline: "Sectors",
        headerTitle: "Engagement across the sectors driving long-term growth.",
        headerLead:
          "Our sector engagement is informed by exposure to infrastructure, development, and investment-related initiatives aligned with national priorities.",
        itemLabel: "Sector",
        noteOverline: "A note on our engagement",
        noteTitle:
          "Sector work at MTBL Management Consultancy is rooted in long-term relationships, disciplined positioning, and alignment with national development agendas.",
        noteBody:
          "We engage selectively, supporting stakeholders across government, private sector, and investment ecosystems to identify, structure, and position opportunities that can contribute to sustainable growth.",
      },

      // ============ OPPORTUNITIES PAGE ============
      opportunities: {
        headerOverline: "Opportunities",
        headerTitle:
          "Engagements across sectors aligned with national development priorities.",
        headerLead:
          "MTBL Management Consultancy engages in opportunities across sectors aligned with national development priorities and economic transformation. Detailed information is shared on a confidential basis.",

        gridOverline: "Selected opportunities",
        gridTitle: "Areas of active engagement.",
        opportunityLabel: "Opportunity",

        confidentialNote:
          "All opportunities are presented at a high level. Detailed briefs are shared on a confidential basis, subject to review.",

        formOverline: "Request investment brief",
        formTitle: "Engage on a confidential basis.",
        formLead:
          "Share a few details and our team will be in touch. Detailed briefs are shared on a confidential basis, subject to review.",
        form: {
          fullName: "Full name*",
          email: "Email*",
          organization: "Organization*",
          title: "Title / Role",
          country: "Country",
          phone: "Phone",
          sectorPlaceholder: "Sector of interest",
          investorPlaceholder: "Investor type",
          message: "Briefly describe your interest",
          submit: "Request Investment Brief",
          submitting: "Submitting…",
          consent:
            "By submitting, you consent to MTBL Management Consultancy contacting you regarding your inquiry. Detailed briefs are shared on a confidential basis, subject to review.",
        },
        toast: {
          missing: "Please complete full name, email, and organization.",
          success:
            "Request received. Detailed briefs are shared on a confidential basis, subject to review.",
          error: "Unable to submit. Please try again or email us directly.",
        },
        sectorOptions: [
          "Telecommunications",
          "Energy and Utilities",
          "Renewable Energy",
          "Oil and Gas",
          "Agriculture",
          "Tourism and Investment Promotion",
          "Infrastructure and Development",
        ],
        investorOptions: [
          "Institutional Investor",
          "Development Finance",
          "Strategic Partner",
          "Family Office",
          "Corporate",
          "Other",
        ],
      },

      // ============ IMPACT PAGE ============
      impact: {
        headerOverline: "Impact",
        headerTitle:
          "Growth that contributes to long-term economic development.",
        headerLead:
          "MTBL Management Consultancy believes sustainable growth should contribute to long-term economic development and meaningful community value. We integrate impact into our work by supporting opportunities that contribute to infrastructure, economic participation, and long-term growth.",

        presenceOverline: "Global presence",
        presenceTitle: "Engaged across five markets, anchored in Africa.",
        presenceLead:
          "Our footprint reflects where we engage alongside partners, institutions, and opportunities across high-growth and established markets.",
        mapOverline: "Global presence",

        pillarsOverline: "Our framework",
        pillarsTitle: "Three considerations shape our impact lens.",

        csrOverline: "Corporate social responsibility",
        csrTitle:
          "A structured CSR framework focused on empowerment, dignity, and community development.",
        csrP1:
          "We are developing a structured corporate social responsibility framework focused on empowerment, dignity, and community development, aligned with our long-term approach to value creation.",
        csrP2:
          "Our engagement prioritises initiatives that support local economic participation, skills development, and meaningful community value across the markets in which we operate.",

        ctaTitle: "Explore partnership opportunities with MTBL Management Consultancy.",
      },

      // ============ PARTNERSHIPS PAGE ============
      partnerships: {
        headerOverline: "Partnerships",
        headerTitle: "Partnerships and Engagements.",
        headerLead:
          "MTBL Management Consultancy works in collaboration with stakeholders across public and private sectors, providing advisory, opportunity positioning, and partnership facilitation.",

        introOverline: "How we collaborate",
        introTitle: "Advisory, transaction, and sector participants.",
        introP1:
          "Our engagements include collaboration with institutions, transaction advisory firms, and sector participants involved in capital mobilization, infrastructure development, and investment facilitation.",
        introP2:
          "Selected partnerships and collaborations are shared where appropriate and within the context of specific engagements.",

        logosOverline: "Selected partners & clients",
        logosTitle: "A selection of our network.",

        ctaTitle: "Interested in collaborating with MTBL Management Consultancy?",
      },

      // ============ INSIGHTS PAGE ============
      insights: {
        headerOverline: "Insights",
        headerTitle:
          "Considered perspectives on capital, markets, and opportunity.",
        headerLead:
          "A selection of perspectives on investment trends, emerging markets strategy, and the sectors shaping long-term growth across Africa and beyond.",
        gridOverline: "Articles",
        gridTitle: "Perspectives from the platform.",
        empty: "No perspectives in this category yet.",
        relatedOverline: "Continue reading",
        relatedTitle: "More perspectives from the platform",
        categories: {
          all: "All",
          africa: "Africa Investment Trends",
          emerging: "Emerging Markets Strategy",
          telecom: "Telecommunications & Infrastructure",
          energy: "Energy & Development",
          tourism: "Tourism & Investment",
        },
      },

      // ============ CONTACT PAGE ============
      contact: {
        headerOverline: "Contact",
        headerTitle: "Engage with MTBL Management Consultancy.",
        headerLead:
          "We work with investors, institutions, and strategic partners seeking access to opportunities across emerging markets.",

        directOverline: "Direct contact",
        officesOverline: "Offices",
        engagementOverline: "Types of engagement",
        engagementList: [
          "Investor inquiries",
          "Strategic partnerships",
          "Institutional engagement",
          "General correspondence",
        ],

        formOverline: "Get in touch",
        formTitle: "Tell us about your inquiry.",
        form: {
          fullName: "Full name*",
          email: "Email*",
          organization: "Organization",
          phone: "Phone",
          subject: "Subject",
          message: "Message*",
          submit: "Send Message",
          submitting: "Sending…",
          consent:
            "By submitting, you consent to MTBL Management Consultancy contacting you regarding your inquiry. MTBL Management Consultancy provides strategic advisory and partnership facilitation services. We do not provide financial investment advice or manage investment portfolios.",
        },
        toast: {
          missing: "Please complete name, email, and message.",
          success: "Your message has been received. We will be in touch.",
          error: "Unable to submit. Please email us directly.",
        },
        inquiryOptions: {
          investor: "Investor Inquiries",
          strategic: "Strategic Partnerships",
          institutional: "Institutional Engagement",
          general: "General",
        },
      },

      // ============ DATA: SERVICES, SECTORS, OPPORTUNITIES, etc ============
      data: {
        services: [
          {
            title: "Strategic Advisory",
            body:
              "We provide advisory services to investors, institutions, and partners seeking to understand and access opportunities across emerging markets.",
          },
          {
            title: "Opportunity Positioning",
            body:
              "We support the structuring and positioning of opportunities to enhance visibility and investment readiness.",
          },
          {
            title: "Deal Engagement",
            body:
              "We engage in identifying and shaping opportunities across key sectors aligned with long-term value creation.",
          },
          {
            title: "Partnership Facilitation",
            body:
              "We connect stakeholders across government, the private sector, and investment ecosystems.",
          },
        ],
        sectors: [
          {
            title: "Telecommunications",
            img: "telecom",
            body:
              "Positioning of backbone networks, tower infrastructure, and connectivity initiatives aligned with national digital-expansion priorities. We engage with operators, infrastructure funds, and public-sector stakeholders to identify and shape strategic opportunities across the connectivity value chain.",
          },
          {
            title: "Energy and Utilities",
            img: "energy",
            body:
              "Engagement across generation, transmission, and distribution, from conventional power assets to grid-modernisation programs. We work alongside institutional investors and development partners pursuing reliable, scalable energy opportunities across emerging markets.",
          },
          {
            title: "Renewable Energy",
            img: "renewable",
            body:
              "Advisory support for wind, solar, and hybrid renewable initiatives aligned with transition agendas and long-horizon institutional capital. We position opportunities for partners seeking disciplined entry into Africa's accelerating clean-energy buildout.",
          },
          {
            title: "Oil and Gas",
            img: "oilgas",
            body:
              "Opportunity positioning across upstream, midstream, and downstream value chains in resource-rich emerging markets. We engage selectively on assets and partnerships that combine commercial merit with credible execution pathways.",
          },
          {
            title: "Agriculture",
            img: "agriculture",
            body:
              "Engagement with agribusiness, value-chain, and food-security initiatives that contribute to economic participation and regional productivity, a cornerstone sector of long-term development across the continent.",
          },
          {
            title: "Tourism and Investment Promotion",
            img: "lodge",
            body:
              "Structured positioning of hospitality, conservation, and destination assets within regional investment agendas. We support operators, destination platforms, and public-sector partners building tourism into a durable growth engine.",
          },
          {
            title: "Infrastructure and Development",
            img: "highway",
            body:
              "Advisory engagement across transport, logistics, housing, and public-sector infrastructure programs. Our work supports the sourcing of strategic partners and investors for large-scale initiatives aligned with national development priorities.",
          },
        ],
        opportunities: [
          {
            title: "Telecommunications Infrastructure",
            summary:
              "Positioning of backbone network, tower, and connectivity initiatives aligned with national digital expansion priorities.",
            img: "telecom",
          },
          {
            title: "Energy and Utilities Infrastructure",
            summary:
              "Engagement across generation, transmission, and distribution programs, including renewable and grid modernization opportunities.",
            img: "energy",
          },
          {
            title: "Tourism and Investment Positioning",
            summary:
              "Structured positioning of hospitality, conservation, and destination assets within regional investment agendas.",
            img: "lodge",
          },
          {
            title: "Infrastructure Development Initiatives",
            summary:
              "Advisory engagement across transport, logistics, housing, and public sector infrastructure programs.",
            img: "highway",
          },
        ],
        whyMtbl: [
          "Rooted understanding of African markets",
          "Multi-sector engagement across growth industries",
          "Strong positioning of opportunities for investors and partners",
          "Engagement within established advisory and investment ecosystems",
          "Long-term focus on value creation and strategic alignment",
        ],
        highlights: [
          { k: "Established", v: "2012" },
          { k: "Engagement", v: "Multi-sector" },
          { k: "Exposure", v: "Infrastructure & Development" },
          { k: "Alignment", v: "National priorities" },
          { k: "Focus", v: "Partnership facilitation" },
        ],
        pillars: [
          {
            title: "Infrastructure Contribution",
            body:
              "Supporting opportunities that contribute to infrastructure development, connectivity, and long-term productive capacity.",
          },
          {
            title: "Economic Participation",
            body:
              "Prioritising engagements that expand access, employment, and opportunity for local communities and regional economies.",
          },
          {
            title: "Sustainable Growth",
            body:
              "Integrating sustainability considerations into opportunity positioning, partnership design, and long-term value creation.",
          },
        ],
        offices: [
          {
            city: "Dubai, United Arab Emirates",
            address:
              "Building A1, Dubai Digital Park, Dubai Silicon Oasis, Dubai, United Arab Emirates",
            image: "dubai",
          },
          {
            city: "Johannesburg, South Africa",
            address: "5 Benmore, Sandton, Johannesburg 2196",
            image: "sandton",
          },
          {
            city: "Kampala, Uganda",
            address: "BMK House, Wampewo Road, PO Box 37468",
            image: "kololo",
          },
        ],
        markers: {
          us: "New York, United States",
          ae: "United Arab Emirates",
          ug: "Uganda",
          cd: "Democratic Republic of the Congo",
          za: "South Africa",
        },
        articles: [
          {
            slug: "drc-uae-tourism-infrastructure-corridor",
            title:
              "DRC x UAE: Unlocking a New Tourism & Infrastructure Investment Corridor",
            category: "Tourism & Investment",
            date: "April 2026",
            excerpt:
              "A new investment corridor is quietly taking shape between Central Africa and the Middle East, with the DRC emerging as a high-potential tourism and infrastructure frontier backed by UAE interest.",
            img: "drcRiver",
            heroImg: "drcRiver",
            sections: [
              {
                kind: "paragraph",
                text:
                  "A new investment corridor is quietly taking shape between Central Africa and the Middle East. At the center of it is the Democratic Republic of the Congo, now emerging as a high-potential tourism and infrastructure frontier backed by UAE interest.",
              },
              {
                kind: "paragraph",
                text:
                  "The partnership between the Democratic Republic of the Congo and the United Arab Emirates reflects a broader global shift. Gulf nations are expanding aggressively into Africa, targeting high-growth, underdeveloped markets.",
              },
              {
                kind: "paragraph",
                text:
                  "With rising demand for alternative investment destinations, Africa, particularly the DRC, is becoming a focal point for FDI, tourism expansion, and infrastructure development.",
              },
              {
                kind: "heading",
                text: "An underleveraged tourism market",
              },
              {
                kind: "paragraph",
                text:
                  "The DRC presents one of the most underleveraged tourism markets globally:",
              },
              {
                kind: "list",
                items: [
                  "World-class natural assets, including Virunga National Park",
                  "Vast rainforests and biodiversity in the Congo Basin",
                  "Urban hubs like Kinshasa with cultural and entertainment potential",
                  "Minimal tourism infrastructure despite global appeal",
                ],
              },
              {
                kind: "paragraph",
                text:
                  "This gap creates a first-mover advantage for investors.",
              },
              {
                kind: "image",
                src: "drcResort",
                caption:
                  "Luxury eco-tourism lodges in rainforest settings — a model the DRC is uniquely positioned to scale.",
              },
              {
                kind: "heading",
                text: "What the UAE brings to the corridor",
              },
              {
                kind: "paragraph",
                text:
                  "The UAE brings a proven model of transforming destinations into global tourism hubs:",
              },
              {
                kind: "list",
                items: [
                  "Infrastructure development across aviation, hospitality, and logistics",
                  "Global connectivity via hubs such as Dubai International Airport",
                  "Access to sovereign wealth capital and private investors",
                  "Expertise in luxury tourism and destination branding",
                ],
              },
              { kind: "heading", text: "The investment thesis" },
              {
                kind: "list",
                items: [
                  "Development of luxury eco-tourism lodges and resorts",
                  "Investment in airport, road, and hospitality infrastructure",
                  "Creation of integrated tourism corridors",
                  "Strategic destination branding and international positioning",
                ],
              },
              {
                kind: "paragraph",
                text:
                  "Global investors are actively seeking untapped, high-return markets. With increasing competition in established destinations, the DRC offers a rare opportunity to enter early in a market with significant upside potential.",
              },
              { kind: "heading", text: "MTBL Management Consultancy's role" },
              {
                kind: "paragraph",
                text:
                  "MTBL Management Consultancy is uniquely positioned to bridge this opportunity, connecting governments, investors, and strategic partners while driving branding, market entry, and project execution across emerging markets.",
              },
              {
                kind: "paragraph",
                text:
                  "The DRC is no longer a hidden opportunity, it is becoming a strategic destination. Backed by UAE partnerships, it stands at the beginning of a transformative tourism and investment journey.",
              },
            ],
          },
          {
            title: "Africa Investment Trends: Positioning for the Next Decade",
            category: "Africa Investment Trends",
            date: "March 2026",
            excerpt:
              "A considered view on capital flows, sector readiness, and how institutional investors are approaching African markets in a shifting global context.",
            img: "skyline",
          },
          {
            title: "Emerging Markets Strategy: Beyond the Thematic Narrative",
            category: "Emerging Markets Strategy",
            date: "February 2026",
            excerpt:
              "Moving past broad thematic labels, we examine how disciplined opportunity positioning creates durable value in emerging markets.",
            img: "boardroom",
          },
          {
            title:
              "Telecommunications and Infrastructure: The Connective Tissue",
            category: "Telecommunications & Infrastructure",
            date: "January 2026",
            excerpt:
              "How connectivity infrastructure is shaping competitiveness, national development agendas, and cross-border investment priorities.",
            img: "telecom",
          },
          {
            title:
              "Energy and Development: Capital, Transition, and Reliability",
            category: "Energy & Development",
            date: "December 2025",
            excerpt:
              "Balancing transition ambitions with reliability needs, a framework for evaluating energy opportunities in growth markets.",
            img: "energy",
          },
          {
            title:
              "Tourism and Investment Positioning in Frontier Destinations",
            category: "Tourism & Investment",
            date: "November 2025",
            excerpt:
              "Structuring tourism assets and destination platforms to attract long-horizon institutional capital.",
            img: "lodge",
          },
          {
            title: "Agriculture, Value Chains, and Long-Term Partnerships",
            category: "Africa Investment Trends",
            date: "October 2025",
            excerpt:
              "Why agriculture remains a cornerstone of regional development, and where disciplined partnership models are emerging.",
            img: "agriculture",
          },
        ],
      },
    },
  },

  fr: {
    translation: {
      // ============ NAV / HEADER ============
      nav: {
        home: "Accueil",
        about: "À propos",
        services: "Services",
        sectors: "Secteurs",
        opportunities: "Opportunités",
        impact: "Impact",
        partnerships: "Partenariats",
        insights: "Analyses",
        contact: "Contact",
        engageCta: "Collaborer avec nous",
        toggleMenu: "Ouvrir le menu",
      },

      // ============ FOOTER ============
      footer: {
        about:
          "MTBL Management Consultancy est un cabinet stratégique de branding, marketing et conseil en investissement, qui relie les capitaux aux opportunités à travers l'Afrique et le Moyen-Orient.",
        positioning: "Vision globale. Ancrage africain.",
        offices: "Bureaux",
        direct: "Contact direct",
        copyright: "© {{year}} MTBL Management Consultancy. Tous droits réservés.",
        poweredBy: "Propulsé par MTBL Management Consultancy",
        disclaimer:
          "MTBL Management Consultancy fournit des services de conseil stratégique et de facilitation de partenariats. Nous ne fournissons pas de conseils en investissement financier et ne gérons pas de portefeuilles d'investissement.",
      },

      // ============ COMMON ============
      common: {
        readMore: "Lire la suite",
        learnMore: "En savoir plus",
        readPerspective: "Lire l'analyse",
        backToInsights: "Retour aux analyses",
        allPerspectives: "Toutes les analyses",
        engageWithMtbl: "Collaborer avec MTBL Management Consultancy",
        viewOpportunities: "Voir les opportunités",
        partnerWithUs: "Devenir partenaire",
        requestInvestmentBrief: "Demander un dossier d'investissement",
        startConversation: "Démarrer la conversation",
        strategicPartnerships: "Partenariats stratégiques",
        investorInquiries: "Demandes des investisseurs",
        institutionalEngagement: "Engagement institutionnel",
        sendMessage: "Envoyer le message",
        sending: "Envoi…",
        submitting: "Envoi…",
      },

      // ============ HERO SLIDESHOW ============
      hero: {
        overline: "Fondé en 2012 · Vision globale · Ancrage africain",
        headlinePart1: "Relier les capitaux aux",
        headlinePart2: "opportunités",
        headlinePart3: "à travers l'Afrique et les marchés émergents",
        support:
          "Fondé en 2012, MTBL Management Consultancy s'engage sur des opportunités alignées avec les priorités de développement national, l'expansion des infrastructures et la croissance économique à long terme.",
      },

      // ============ HOME PAGE ============
      home: {
        positioning1: "Vision globale. Ancrage africain.",
        positioning2: "Positionné sur les marchés à forte croissance.",

        whatWeDoOverline: "Ce que nous faisons",
        whatWeDoTitle:
          "À la croisée du conseil, de l'engagement en investissement et de la collaboration transfrontalière.",
        whatWeDoP1:
          "MTBL Management Consultancy opère à la croisée du conseil, de l'engagement en investissement et de la collaboration transfrontalière.",
        whatWeDoP2:
          "Nous accompagnons l'identification, le positionnement et la facilitation de partenariats sur des opportunités, dans les secteurs essentiels à la croissance et au développement économique.",
        whatWeDoLearnMore: "En savoir plus sur MTBL Management Consultancy",

        servicesOverline: "Services",
        servicesTitle: "Notre approche d'engagement.",
        servicesDisclaimer:
          "MTBL Management Consultancy fournit des services de conseil stratégique et de facilitation de partenariats. Nous ne fournissons pas de conseils en investissement financier et ne gérons pas de portefeuilles d'investissement.",

        opportunitiesOverline: "Opportunités sélectionnées",
        opportunitiesTitle:
          "Des opportunités alignées avec les priorités de développement national.",
        opportunitiesLead:
          "MTBL Management Consultancy s'engage sur des opportunités alignées avec les priorités de développement national et la transformation économique. Les informations détaillées sont partagées sur une base confidentielle.",
        opportunityLabel: "Opportunité",

        engagementOverline: "Engagement stratégique",
        engagementTitle: "Aligné avec les agendas de développement national.",
        engagementP1:
          "MTBL Management Consultancy s'investit dans des initiatives alignées avec les agendas de développement national et les programmes d'infrastructure d'envergure.",
        engagementP2:
          "Nos missions incluent l'identification de partenaires stratégiques et d'investisseurs dans les transports, l'énergie, les TIC, la logistique, le logement et les programmes du secteur public.",

        collaborationOverline: "Collaboration stratégique",
        collaborationTitle:
          "Aux côtés de partenaires régionaux et internationaux.",
        collaborationP1:
          "MTBL Management Consultancy collabore avec des partenaires régionaux et internationaux sur plusieurs marchés.",
        collaborationP2:
          "Nos engagements incluent le travail conjoint avec des cabinets de conseil en transactions et des institutions impliquées dans la mobilisation de capitaux, l'identification de partenaires et la facilitation d'investissements.",

        whyOverline: "Pourquoi MTBL Management Consultancy",
        whyTitle: "Une plateforme rigoureuse, axée sur les relations.",

        highlightsOverline: "Repères",
        highlightsTitle: "Un parcours d'engagement ciblé.",

        impactOverline: "Impact",
        impactTitle:
          "Une croissance qui contribue au développement à long terme.",
        impactP1:
          "MTBL Management Consultancy estime qu'une croissance durable doit contribuer au développement économique à long terme et apporter une réelle valeur aux communautés.",
        impactP2:
          "Nous intégrons cette dimension d'impact dans nos missions en soutenant des opportunités qui contribuent aux infrastructures, à la participation économique et à la croissance pérenne.",
        impactQuote:
          "Nous développons un cadre structuré de responsabilité sociétale axé sur l'autonomisation, la dignité et le développement communautaire.",

        finalCtaOverline: "Travaillons ensemble",
        finalCtaTitle:
          "L'accès aux opportunités sur les marchés émergents commence par une conversation.",
        finalCtaLead:
          "Nous accompagnons les investisseurs, institutions et partenaires stratégiques en quête d'accès aux opportunités sur les marchés émergents.",
      },

      // ============ ABOUT PAGE ============
      about: {
        overline: "Qui nous sommes",
        title: "Qui nous sommes",
        introP1:
          "MTBL Management Consultancy est un cabinet stratégique de branding, marketing et conseil en investissement, dédié à révéler des opportunités de croissance à fort impact à travers l'Afrique et le Moyen-Orient.",
        introP2:
          "Nous mettons en relation gouvernements, entreprises et investisseurs grâce à des solutions créatives, une stratégie fondée sur les données et des partenariats puissants qui transforment les idées en opportunités scalables et prêtes à l'investissement.",
        introP3:
          "Ancré en Afrique et basé à Sandton, en Afrique du Sud, MTBL Management Consultancy opère à la croisée de la stratégie, de la créativité et du capital, positionnant marchés, marques et institutions pour une croissance durable et une portée mondiale.",

        valuesOverline: "Nos valeurs fondamentales",
        valuesTitle:
          "Construit sur l'intégrité, l'honnêteté et de solides standards éthiques.",
        valuesP1:
          "Chez MTBL Management Consultancy, notre fondement repose sur l'intégrité, l'honnêteté et de solides standards éthiques.",
        valuesP2:
          "Ces principes guident chacune de nos décisions et façonnent notre manière d'interagir avec nos clients, partenaires et communautés. Nous croyons que la confiance est la pierre angulaire de tout partenariat réussi, et nous nous engageons à délivrer avec transparence, responsabilité et excellence.",

        missionOverline: "Notre mission",
        missionTitle:
          "Connecter marques, investisseurs et entreprises à des opportunités transformatrices.",
        missionP1:
          "Connecter marques, investisseurs et entreprises à des opportunités transformatrices grâce à des services de branding, de marketing et de conseil stratégique, en générant un impact économique durable et une croissance pérenne.",

        visionOverline: "Notre vision",
        visionTitle:
          "La plateforme de référence pour le branding, le marketing et la facilitation d'investissement.",
        visionP1:
          "Devenir la plateforme de référence pour le branding, le marketing et la facilitation d'investissement à travers l'Afrique et le Moyen-Orient, en positionnant les marchés émergents comme moteurs de la croissance mondiale.",

        experienceOverline: "Notre expérience",
        experienceTitle:
          "Des décennies d'expérience cumulée dans plusieurs industries.",
        experienceP1:
          "Notre équipe combine des décennies d'expérience dans de multiples industries, dont les télécoms, l'énergie, les infrastructures, le tourisme et la finance.",
        experienceP2:
          "Nous avons accompagné des organisations de toutes tailles, des startups aux multinationales, ce qui nous donne une compréhension fine des défis et opportunités propres aux marchés émergents.",
        experienceP3prefix: "Avec plus de",
        experienceP3highlight: "100 millions $ de transactions facilitées",
        experienceP3suffix:
          ", MTBL Management Consultancy conjugue vision stratégique et capacité d'exécution, livrant des résultats qui font bouger les marchés, attirent l'investissement et créent un impact durable.",
        statDealLabel: "Transactions facilitées",
        statMarketsLabel: "Marchés",
        statEstablishedLabel: "Fondé en",

        ctaTitle: "Découvrez les opportunités sur lesquelles nous intervenons.",
      },

      // ============ SERVICES PAGE ============
      services: {
        headerOverline: "Services",
        headerTitle: "Conseil, positionnement, engagement et facilitation.",
        headerLead:
          "Nous accompagnons investisseurs, institutions et partenaires stratégiques tout au long du cycle d'engagement sur les opportunités, de l'identification au positionnement structuré et à la collaboration durable.",
        itemLabel: "Service",
        disclaimer:
          "MTBL Management Consultancy fournit des services de conseil stratégique et de facilitation de partenariats. Nous ne fournissons pas de conseils en investissement financier et ne gérons pas de portefeuilles d'investissement.",
        ctaTitle: "Engagez MTBL Management Consultancy sur un mandat spécifique.",
      },

      // ============ SECTORS PAGE ============
      sectors: {
        headerOverline: "Secteurs",
        headerTitle:
          "Engagement dans les secteurs moteurs de la croissance à long terme.",
        headerLead:
          "Notre engagement sectoriel s'appuie sur une exposition aux infrastructures, au développement et aux initiatives d'investissement alignées avec les priorités nationales.",
        itemLabel: "Secteur",
        noteOverline: "Une note sur notre engagement",
        noteTitle:
          "Le travail sectoriel de MTBL Management Consultancy s'enracine dans des relations de long terme, un positionnement rigoureux et un alignement avec les agendas de développement national.",
        noteBody:
          "Nous nous engageons de manière sélective, en accompagnant les acteurs publics, privés et de l'écosystème d'investissement pour identifier, structurer et positionner des opportunités contribuant à une croissance durable.",
      },

      // ============ OPPORTUNITIES PAGE ============
      opportunities: {
        headerOverline: "Opportunités",
        headerTitle:
          "Engagements dans des secteurs alignés avec les priorités de développement national.",
        headerLead:
          "MTBL Management Consultancy s'engage sur des opportunités dans des secteurs alignés avec les priorités de développement national et la transformation économique. Les informations détaillées sont partagées sur une base confidentielle.",

        gridOverline: "Opportunités sélectionnées",
        gridTitle: "Domaines d'engagement actif.",
        opportunityLabel: "Opportunité",

        confidentialNote:
          "Toutes les opportunités sont présentées de manière synthétique. Les dossiers détaillés sont partagés sur une base confidentielle, sous réserve de revue.",

        formOverline: "Demande de dossier d'investissement",
        formTitle: "Engagez-vous sur une base confidentielle.",
        formLead:
          "Partagez quelques détails et notre équipe reviendra vers vous. Les dossiers détaillés sont partagés sur une base confidentielle, sous réserve de revue.",
        form: {
          fullName: "Nom complet*",
          email: "E-mail*",
          organization: "Organisation*",
          title: "Titre / Fonction",
          country: "Pays",
          phone: "Téléphone",
          sectorPlaceholder: "Secteur d'intérêt",
          investorPlaceholder: "Type d'investisseur",
          message: "Décrivez brièvement votre intérêt",
          submit: "Demander le dossier d'investissement",
          submitting: "Envoi…",
          consent:
            "En soumettant ce formulaire, vous consentez à ce que MTBL Management Consultancy vous contacte au sujet de votre demande. Les dossiers détaillés sont partagés sur une base confidentielle, sous réserve de revue.",
        },
        toast: {
          missing:
            "Veuillez renseigner le nom complet, l'e-mail et l'organisation.",
          success:
            "Demande reçue. Les dossiers détaillés sont partagés sur une base confidentielle, sous réserve de revue.",
          error:
            "Soumission impossible. Réessayez ou écrivez-nous directement par e-mail.",
        },
        sectorOptions: [
          "Télécommunications",
          "Énergie et services publics",
          "Énergies renouvelables",
          "Pétrole et gaz",
          "Agriculture",
          "Tourisme et promotion d'investissement",
          "Infrastructures et développement",
        ],
        investorOptions: [
          "Investisseur institutionnel",
          "Finance de développement",
          "Partenaire stratégique",
          "Family Office",
          "Entreprise",
          "Autre",
        ],
      },

      // ============ IMPACT PAGE ============
      impact: {
        headerOverline: "Impact",
        headerTitle:
          "Une croissance qui contribue au développement économique à long terme.",
        headerLead:
          "MTBL Management Consultancy estime qu'une croissance durable doit contribuer au développement économique à long terme et apporter une valeur réelle aux communautés. Nous intégrons l'impact dans notre travail en soutenant des opportunités qui contribuent aux infrastructures, à la participation économique et à la croissance pérenne.",

        presenceOverline: "Présence mondiale",
        presenceTitle: "Engagés sur cinq marchés, ancrés en Afrique.",
        presenceLead:
          "Notre empreinte reflète les marchés sur lesquels nous intervenons aux côtés de partenaires, d'institutions et d'opportunités, à fort potentiel comme établis.",
        mapOverline: "Présence mondiale",

        pillarsOverline: "Notre cadre",
        pillarsTitle: "Trois considérations façonnent notre vision d'impact.",

        csrOverline: "Responsabilité sociétale",
        csrTitle:
          "Un cadre RSE structuré, axé sur l'autonomisation, la dignité et le développement communautaire.",
        csrP1:
          "Nous développons un cadre structuré de responsabilité sociétale axé sur l'autonomisation, la dignité et le développement communautaire, en cohérence avec notre approche de création de valeur à long terme.",
        csrP2:
          "Notre engagement privilégie les initiatives qui soutiennent la participation économique locale, le développement des compétences et la création d'une valeur réelle pour les communautés sur les marchés où nous opérons.",

        ctaTitle:
          "Explorez les opportunités de partenariat avec MTBL Management Consultancy.",
      },

      // ============ PARTNERSHIPS PAGE ============
      partnerships: {
        headerOverline: "Partenariats",
        headerTitle: "Partenariats et engagements.",
        headerLead:
          "MTBL Management Consultancy travaille en collaboration avec des acteurs des secteurs public et privé, en fournissant du conseil, du positionnement d'opportunités et de la facilitation de partenariats.",

        introOverline: "Notre manière de collaborer",
        introTitle:
          "Conseil, transactions et acteurs sectoriels.",
        introP1:
          "Nos engagements incluent la collaboration avec des institutions, des cabinets de conseil en transactions et des acteurs sectoriels impliqués dans la mobilisation de capitaux, le développement d'infrastructures et la facilitation d'investissement.",
        introP2:
          "Les partenariats et collaborations sélectionnés sont partagés lorsque cela est approprié et dans le contexte d'engagements spécifiques.",

        logosOverline: "Partenaires & clients sélectionnés",
        logosTitle: "Un aperçu de notre réseau.",

        ctaTitle: "Intéressé par une collaboration avec MTBL Management Consultancy ?",
      },

      // ============ INSIGHTS PAGE ============
      insights: {
        headerOverline: "Analyses",
        headerTitle:
          "Des perspectives réfléchies sur le capital, les marchés et les opportunités.",
        headerLead:
          "Une sélection d'analyses sur les tendances d'investissement, la stratégie sur les marchés émergents et les secteurs qui façonnent la croissance à long terme en Afrique et au-delà.",
        gridOverline: "Articles",
        gridTitle: "Perspectives de la plateforme.",
        empty: "Aucune analyse dans cette catégorie pour le moment.",
        relatedOverline: "Continuer la lecture",
        relatedTitle: "Plus d'analyses de la plateforme",
        categories: {
          all: "Toutes",
          africa: "Tendances d'investissement en Afrique",
          emerging: "Stratégie sur les marchés émergents",
          telecom: "Télécommunications & infrastructures",
          energy: "Énergie & développement",
          tourism: "Tourisme & investissement",
        },
      },

      // ============ CONTACT PAGE ============
      contact: {
        headerOverline: "Contact",
        headerTitle: "Échanger avec MTBL Management Consultancy.",
        headerLead:
          "Nous accompagnons investisseurs, institutions et partenaires stratégiques en quête d'accès aux opportunités sur les marchés émergents.",

        directOverline: "Contact direct",
        officesOverline: "Bureaux",
        engagementOverline: "Types d'engagement",
        engagementList: [
          "Demandes des investisseurs",
          "Partenariats stratégiques",
          "Engagement institutionnel",
          "Correspondance générale",
        ],

        formOverline: "Nous écrire",
        formTitle: "Parlez-nous de votre demande.",
        form: {
          fullName: "Nom complet*",
          email: "E-mail*",
          organization: "Organisation",
          phone: "Téléphone",
          subject: "Sujet",
          message: "Message*",
          submit: "Envoyer le message",
          submitting: "Envoi…",
          consent:
            "En soumettant ce formulaire, vous consentez à ce que MTBL Management Consultancy vous contacte au sujet de votre demande. MTBL Management Consultancy fournit des services de conseil stratégique et de facilitation de partenariats. Nous ne fournissons pas de conseils en investissement financier et ne gérons pas de portefeuilles d'investissement.",
        },
        toast: {
          missing: "Veuillez renseigner le nom, l'e-mail et le message.",
          success:
            "Votre message a bien été reçu. Nous reviendrons vers vous.",
          error:
            "Envoi impossible. Merci de nous écrire directement par e-mail.",
        },
        inquiryOptions: {
          investor: "Demandes des investisseurs",
          strategic: "Partenariats stratégiques",
          institutional: "Engagement institutionnel",
          general: "Général",
        },
      },

      // ============ DATA ============
      data: {
        services: [
          {
            title: "Conseil stratégique",
            body:
              "Nous accompagnons investisseurs, institutions et partenaires souhaitant comprendre et accéder aux opportunités sur les marchés émergents.",
          },
          {
            title: "Positionnement d'opportunités",
            body:
              "Nous accompagnons la structuration et le positionnement des opportunités pour renforcer leur visibilité et leur préparation à l'investissement.",
          },
          {
            title: "Engagement transactionnel",
            body:
              "Nous identifions et façonnons des opportunités dans les secteurs clés, en cohérence avec une création de valeur à long terme.",
          },
          {
            title: "Facilitation de partenariats",
            body:
              "Nous mettons en relation les acteurs des écosystèmes publics, privés et d'investissement.",
          },
        ],
        sectors: [
          {
            title: "Télécommunications",
            img: "telecom",
            body:
              "Positionnement de réseaux dorsaux, d'infrastructures de tours et d'initiatives de connectivité alignées avec les priorités d'expansion numérique nationale. Nous travaillons avec opérateurs, fonds d'infrastructure et acteurs publics pour identifier et façonner des opportunités stratégiques tout au long de la chaîne de valeur de la connectivité.",
          },
          {
            title: "Énergie et services publics",
            img: "energy",
            body:
              "Engagement sur la production, le transport et la distribution, des actifs conventionnels aux programmes de modernisation des réseaux. Nous accompagnons investisseurs institutionnels et partenaires de développement à la recherche d'opportunités énergétiques fiables et scalables sur les marchés émergents.",
          },
          {
            title: "Énergies renouvelables",
            img: "renewable",
            body:
              "Conseil sur les initiatives éoliennes, solaires et hybrides en lien avec les agendas de transition et le capital institutionnel à long horizon. Nous positionnons des opportunités pour les partenaires recherchant une entrée disciplinée dans le déploiement accéléré des énergies propres en Afrique.",
          },
          {
            title: "Pétrole et gaz",
            img: "oilgas",
            body:
              "Positionnement d'opportunités sur les chaînes de valeur amont, intermédiaire et aval, dans des marchés émergents riches en ressources. Nous nous engageons sélectivement sur des actifs et partenariats combinant pertinence commerciale et exécution crédible.",
          },
          {
            title: "Agriculture",
            img: "agriculture",
            body:
              "Engagement sur l'agro-industrie, les chaînes de valeur et les initiatives de sécurité alimentaire qui contribuent à la participation économique et à la productivité régionale, un secteur pilier du développement à long terme du continent.",
          },
          {
            title: "Tourisme et promotion d'investissement",
            img: "lodge",
            body:
              "Positionnement structuré d'actifs hôteliers, de conservation et de destinations dans les agendas d'investissement régionaux. Nous accompagnons opérateurs, plateformes de destination et partenaires publics qui font du tourisme un moteur de croissance durable.",
          },
          {
            title: "Infrastructures et développement",
            img: "highway",
            body:
              "Conseil sur les programmes de transport, logistique, logement et infrastructures publiques. Notre travail soutient la recherche de partenaires stratégiques et d'investisseurs pour des initiatives à grande échelle alignées avec les priorités de développement national.",
          },
        ],
        opportunities: [
          {
            title: "Infrastructures de télécommunications",
            summary:
              "Positionnement d'initiatives de réseaux dorsaux, de tours et de connectivité alignées avec les priorités d'expansion numérique nationale.",
            img: "telecom",
          },
          {
            title: "Infrastructures énergétiques et de services publics",
            summary:
              "Engagement sur les programmes de production, transport et distribution, incluant les opportunités renouvelables et de modernisation des réseaux.",
            img: "energy",
          },
          {
            title: "Positionnement tourisme et investissement",
            summary:
              "Positionnement structuré d'actifs hôteliers, de conservation et de destinations dans les agendas d'investissement régionaux.",
            img: "lodge",
          },
          {
            title: "Initiatives de développement d'infrastructures",
            summary:
              "Conseil sur les programmes de transport, logistique, logement et infrastructures publiques.",
            img: "highway",
          },
        ],
        whyMtbl: [
          "Compréhension profonde des marchés africains",
          "Engagement multisectoriel sur les industries de croissance",
          "Positionnement solide d'opportunités pour investisseurs et partenaires",
          "Engagement au sein d'écosystèmes de conseil et d'investissement établis",
          "Focus long terme sur la création de valeur et l'alignement stratégique",
        ],
        highlights: [
          { k: "Fondé en", v: "2012" },
          { k: "Engagement", v: "Multisectoriel" },
          { k: "Exposition", v: "Infrastructures & développement" },
          { k: "Alignement", v: "Priorités nationales" },
          { k: "Focus", v: "Facilitation de partenariats" },
        ],
        pillars: [
          {
            title: "Contribution aux infrastructures",
            body:
              "Soutenir des opportunités qui contribuent au développement des infrastructures, à la connectivité et à la capacité productive de long terme.",
          },
          {
            title: "Participation économique",
            body:
              "Privilégier les engagements qui élargissent l'accès, l'emploi et les opportunités pour les communautés locales et les économies régionales.",
          },
          {
            title: "Croissance durable",
            body:
              "Intégrer la durabilité dans le positionnement des opportunités, la conception des partenariats et la création de valeur à long terme.",
          },
        ],
        offices: [
          {
            city: "Dubaï, Émirats arabes unis",
            address:
              "Bâtiment A1, Dubai Digital Park, Dubai Silicon Oasis, Dubaï, Émirats arabes unis",
            image: "dubai",
          },
          {
            city: "Johannesburg, Afrique du Sud",
            address: "5 Benmore, Sandton, Johannesburg 2196",
            image: "sandton",
          },
          {
            city: "Kampala, Ouganda",
            address: "BMK House, Wampewo Road, PO Box 37468",
            image: "kololo",
          },
        ],
        markers: {
          us: "New York, États-Unis",
          ae: "Émirats arabes unis",
          ug: "Ouganda",
          cd: "République démocratique du Congo",
          za: "Afrique du Sud",
        },
        articles: [
          {
            slug: "drc-uae-tourism-infrastructure-corridor",
            title:
              "RDC × ÉAU : un nouveau corridor d'investissement tourisme & infrastructures",
            category: "Tourisme & investissement",
            date: "Avril 2026",
            excerpt:
              "Un nouveau corridor d'investissement prend discrètement forme entre l'Afrique centrale et le Moyen-Orient, avec la RDC qui s'impose comme une frontière à fort potentiel pour le tourisme et les infrastructures, soutenue par l'intérêt émirati.",
            img: "drcRiver",
            heroImg: "drcRiver",
            sections: [
              {
                kind: "paragraph",
                text:
                  "Un nouveau corridor d'investissement prend discrètement forme entre l'Afrique centrale et le Moyen-Orient. En son centre, la République démocratique du Congo, qui s'impose désormais comme une frontière à fort potentiel pour le tourisme et les infrastructures, portée par l'intérêt émirati.",
              },
              {
                kind: "paragraph",
                text:
                  "Le partenariat entre la République démocratique du Congo et les Émirats arabes unis reflète une dynamique mondiale plus large. Les nations du Golfe se déploient activement en Afrique, ciblant des marchés à forte croissance et sous-développés.",
              },
              {
                kind: "paragraph",
                text:
                  "Avec la demande croissante pour des destinations d'investissement alternatives, l'Afrique, et particulièrement la RDC, devient un point focal pour les IDE, l'expansion touristique et le développement des infrastructures.",
              },
              {
                kind: "heading",
                text: "Un marché touristique sous-exploité",
              },
              {
                kind: "paragraph",
                text:
                  "La RDC présente l'un des marchés touristiques les plus sous-exploités au monde :",
              },
              {
                kind: "list",
                items: [
                  "Des actifs naturels de classe mondiale, notamment le parc national des Virunga",
                  "De vastes forêts tropicales et une biodiversité unique dans le bassin du Congo",
                  "Des centres urbains comme Kinshasa, à fort potentiel culturel et de divertissement",
                  "Une infrastructure touristique encore minimale malgré un attrait global",
                ],
              },
              {
                kind: "paragraph",
                text:
                  "Cet écart crée un avantage de pionnier pour les investisseurs.",
              },
              {
                kind: "image",
                src: "drcResort",
                caption:
                  "Lodges d'écotourisme de luxe en cadre forestier, un modèle que la RDC est uniquement positionnée pour déployer à grande échelle.",
              },
              {
                kind: "heading",
                text: "Ce que les ÉAU apportent au corridor",
              },
              {
                kind: "paragraph",
                text:
                  "Les ÉAU apportent un modèle éprouvé de transformation des destinations en hubs touristiques mondiaux :",
              },
              {
                kind: "list",
                items: [
                  "Développement d'infrastructures dans l'aviation, l'hôtellerie et la logistique",
                  "Connectivité mondiale via des hubs comme l'aéroport international de Dubaï",
                  "Accès aux capitaux souverains et aux investisseurs privés",
                  "Expertise dans le tourisme de luxe et le branding de destination",
                ],
              },
              { kind: "heading", text: "La thèse d'investissement" },
              {
                kind: "list",
                items: [
                  "Développement de lodges et resorts d'écotourisme de luxe",
                  "Investissement dans les infrastructures aéroportuaires, routières et hôtelières",
                  "Création de corridors touristiques intégrés",
                  "Branding stratégique de destination et positionnement international",
                ],
              },
              {
                kind: "paragraph",
                text:
                  "Les investisseurs mondiaux recherchent activement des marchés à fort rendement et encore inexploités. Avec la concurrence croissante sur les destinations établies, la RDC offre une rare opportunité d'entrée précoce sur un marché à fort potentiel de hausse.",
              },
              { kind: "heading", text: "Le rôle de MTBL Management Consultancy" },
              {
                kind: "paragraph",
                text:
                  "MTBL Management Consultancy est uniquement positionné pour faire le pont sur cette opportunité, en connectant gouvernements, investisseurs et partenaires stratégiques, tout en pilotant le branding, l'entrée sur le marché et l'exécution de projets sur les marchés émergents.",
              },
              {
                kind: "paragraph",
                text:
                  "La RDC n'est plus une opportunité cachée, elle devient une destination stratégique. Soutenue par les partenariats avec les ÉAU, elle se trouve au commencement d'une trajectoire transformatrice pour le tourisme et l'investissement.",
              },
            ],
          },
          {
            title:
              "Tendances d'investissement en Afrique : se positionner pour la prochaine décennie",
            category: "Tendances d'investissement en Afrique",
            date: "Mars 2026",
            excerpt:
              "Une lecture pondérée des flux de capitaux, de la maturité sectorielle et de la manière dont les investisseurs institutionnels abordent les marchés africains dans un contexte mondial en mutation.",
            img: "skyline",
          },
          {
            title:
              "Stratégie sur les marchés émergents : au-delà du récit thématique",
            category: "Stratégie sur les marchés émergents",
            date: "Février 2026",
            excerpt:
              "Au-delà des grandes étiquettes thématiques, nous examinons comment un positionnement d'opportunités rigoureux crée une valeur durable sur les marchés émergents.",
            img: "boardroom",
          },
          {
            title:
              "Télécommunications et infrastructures : le tissu connectif",
            category: "Télécommunications & infrastructures",
            date: "Janvier 2026",
            excerpt:
              "Comment les infrastructures de connectivité façonnent la compétitivité, les agendas de développement national et les priorités d'investissement transfrontalières.",
            img: "telecom",
          },
          {
            title:
              "Énergie et développement : capital, transition et fiabilité",
            category: "Énergie & développement",
            date: "Décembre 2025",
            excerpt:
              "Concilier les ambitions de transition avec les besoins de fiabilité, un cadre pour évaluer les opportunités énergétiques sur les marchés en croissance.",
            img: "energy",
          },
          {
            title:
              "Positionnement tourisme et investissement dans les destinations frontières",
            category: "Tourisme & investissement",
            date: "Novembre 2025",
            excerpt:
              "Structurer les actifs touristiques et les plateformes de destination pour attirer du capital institutionnel à long horizon.",
            img: "lodge",
          },
          {
            title:
              "Agriculture, chaînes de valeur et partenariats de long terme",
            category: "Tendances d'investissement en Afrique",
            date: "Octobre 2025",
            excerpt:
              "Pourquoi l'agriculture demeure une pierre angulaire du développement régional, et où émergent des modèles de partenariat rigoureux.",
            img: "agriculture",
          },
        ],
      },
    },
  },
};

const initialLang =
  (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "en";

i18n.use(initReactI18next).init({
  resources,
  lng: initialLang,
  fallbackLng: "en",
  interpolation: { escapeValue: false },
  returnObjects: true,
});

export const setLanguage = (lang) => {
  i18n.changeLanguage(lang);
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }
};

if (typeof window !== "undefined") {
  document.documentElement.lang = initialLang;
}

export default i18n;
